<script setup>
import { computed, ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { api } from '../services/api';

const router = useRouter();


const currPage = ref(1);
const blogData = ref([]);
const pages = ref(1);
const isLoading = ref(false); // New reactive state for loading


async function getBlogData(pageNum) {
    if (isLoading.value) return; // Prevent concurrent fetches
    isLoading.value = true; // Set loading to true before fetching
    try{
        const data = await api.getPosts(pageNum);
        blogData.value = data.items;
        currPage.value = data.page;
        pages.value = data.pages;
    } catch(error) {
        console.error("Caught an error in getBlogData:", error); // More specific error log
    } finally {
        isLoading.value = false; // Set loading to false after fetch completes
    }
}

onMounted(() => {
    getBlogData(1);
});

function goToPage(pageNum) {
    if (pageNum < 1 || pageNum > pages.value) return
    getBlogData(pageNum);
}

const hasPosts = computed(() => {
    return Array.isArray(blogData.value) && blogData.value.length > 0;
});
</script>

<template>
    <div class="bg-gray-100 py-16">
        <div class="container mx-auto px-4">
            <header class="text-center mb-10 md:mb-16" v-reveal:up>
                <h1 class="text-4xl md:text-5xl font-extrabold text-gray-800">Our Blog</h1>
                <p class="text-lg text-gray-600 mt-2">Insights, stories, and news from our team.</p>
            </header>

            <!-- Loading Skeleton -->
            <div v-if="isLoading" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
                <div v-for="n in 3" :key="n" class="bg-white rounded-xl shadow-lg overflow-hidden animate-pulse">
                    <div class="w-full h-56 bg-gray-300"></div>
                    <div class="p-6">
                        <div class="h-6 bg-gray-300 rounded w-3/4 mb-3"></div>
                        <div class="h-4 bg-gray-300 rounded w-1/4 mb-5"></div>
                        <div class="h-10 bg-gray-400 rounded w-32"></div>
                    </div>
                </div>
            </div>

                    <!-- Blog Posts Grid -->
            <!-- Blog Posts Grid -->
            <div v-else-if="hasPosts" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10">
                <div v-for="(post, index) in blogData" :key="post.id" v-reveal:up="index * 100" class="group bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
                    <img :src="post.img_path" :alt="post.title" class="w-full h-56 object-cover transition-transform duration-300 group-hover:scale-105"/>
                    <div class="p-6">
                        <h2 class="text-2xl font-bold text-gray-800 mb-3">{{ post.title }}</h2>
                        <div class="flex items-center justify-between text-gray-500 text-xs">
                            <span>{{ new Date(post.created_at).toLocaleDateString() }}</span>
                        </div>
                        <button @click="router.push({ name: 'view-blog', params: { postId: post.id } })" class="mt-5 inline-block bg-primary text-white font-semibold py-2 px-4 rounded-lg hover:bg-primary/90 transition duration-300">
                            Read More
                        </button>
                    </div>
                </div>
            </div>

            <!-- No Posts Found Message -->
            <div v-else class="text-center py-16">
                <h2 class="text-2xl font-semibold text-gray-700">No Posts Found</h2>
                <p class="text-gray-500 mt-2">There are currently no blog posts available. Please check back later.</p>
            </div>

            <!-- Pagination Controls -->
            <nav class="flex justify-center mt-12 md:mt-16" aria-label="Pagination">
                <ul class="inline-flex items-center -space-x-px">
                     <li>
                        <button
                            @click="goToPage(currPage - 1)"
                            :disabled="currPage === 1"
                            class="pagination-arrow"
                            aria-label="Previous Page"
                        >
                            <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clip-rule="evenodd"></path></svg>
                        </button>
                    </li>
                    <li v-for="page in pages" :key="page">
                        <button
                            @click="goToPage(page)"
                            :class="page === currPage ? 'pagination-item-active' : 'pagination-item'"
                        >
                            {{ page }}
                        </button>
                    </li>
                    <li>
                         <button
                            @click="goToPage(currPage + 1)"
                            :disabled="currPage === pages"
                            class="pagination-arrow"
                            aria-label="Next Page"
                        >
                            <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clip-rule="evenodd"></path></svg>
                        </button>
                    </li>
                </ul>
            </nav>
        </div>
    </div>
</template>

<style scoped>
/* Base classes for pagination styling */
.pagination-item, .pagination-arrow {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0.5rem 1rem;
    margin: 0 2px;
    border-radius: 0.5rem;
    border: 1px solid #d1d5db; /* gray-300 */
    background-color: #ffffff;
    color: #374151; /* gray-700 */
    transition: all 0.2s ease-in-out;
    font-weight: 500;
}

.pagination-item:hover, .pagination-arrow:hover {
    background-color: #f3f4f6; /* gray-100 */
    border-color: #9ca3af; /* gray-400 */
}

.pagination-arrow:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background-color: #f9fafb; /* gray-50 */
}

.pagination-item-active {
    /* Apply styles from .pagination-item but with active state colors */
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0.5rem 1rem;
    margin: 0 2px;
    border-radius: 0.5rem;
    border: 1px solid #D62828;
    background-color: #D62828;
    color: #ffffff;
    font-weight: 600;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.bg-primary { background-color: #D62828; }
.text-primary { color: #D62828; }
.hover\:bg-primary\/90:hover { background-color: #b02020; }
</style>